'use client';

import { useCallback } from 'react';
import { BN } from '@drift-labs/sdk';
import { getTokenAddressForDepositAndWithdraw, UIMarket } from '@drift/common';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import { notify } from 'src/utils/notifications';
import { useGetTxParams } from '../transactions/useGetTxParams';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import { updateAfterTx } from 'src/stores/DriftStore/helpers/transactionHelpers';
import type { BaseMessageSignerWalletAdapter as Adapter } from '@solana/wallet-adapter-base';

interface AddInsuranceFundStakeOptions {
	amount: BN;
	marketIndex: number;
	/**
	 * If true, also tries to create a new IF account at the same time. Use first time the user does it.
	 */
	initializeStakeAccount?: boolean;
	/**
	 * If true, tries to withdraw the amount from the current subaccount instead of using wallet balance
	 */
	fromSubaccount?: boolean;
	/**
	 * if fromSubaccount, pass userId of the subaccount
	 */
	userId?: number;
	/**
	 * if fromSubaccount, pass userKey of the subaccount
	 */
	userKey?: string;
}

export const useAddInsuranceFundStake = () => {
	const driftClient = useDriftStore((s) => s.driftClient.client);
	const wallet = useDriftStore((s) => s.wallet.current.adapter);
	const accountsGet = useDriftAccountsStore((s) => s.get);
	const getTxParams = useGetTxParams();

	const addInsuranceFundStake = useCallback(
		async (opts: AddInsuranceFundStakeOptions): Promise<boolean> => {
			if (!driftClient || !wallet?.publicKey) return false;

			const accountsState = accountsGet();

			if (opts.userId !== undefined) {
				await driftClient.switchActiveUser(opts.userId);
			}

			const spotMarketConfig = UIMarket.spotMarkets.find(
				(m) => m.marketIndex === opts.marketIndex
			);

			const toastId = `add-stake-to-if-${opts.marketIndex}`;

			notify({
				id: toastId,
				type: 'awaiting_wallet_confirmation',
				showUntilCancelled: true,
			});

			try {
				const spotMarketAccount = driftClient.getSpotMarketAccount(
					spotMarketConfig?.marketIndex ?? 0
				);

				if (!spotMarketAccount) {
					throw new Error('Spot market account not found');
				}

				const tokenAccount = await getTokenAddressForDepositAndWithdraw(
					spotMarketAccount,
					wallet.publicKey
				);

				const _txid = await driftClient?.addInsuranceFundStake({
					...opts,
					collateralAccountPublicKey: tokenAccount,
					txParams: getTxParams(),
				});

				notify({
					id: toastId,
					type: 'success',
					message: `Staked ${spotMarketConfig?.symbol}.`,
					showUntilCancelled: false,
				});

				if (opts.userId !== undefined && opts.userKey) {
					const userClient = accountsState.accounts[opts.userKey]?.client;
					if (userClient) {
						updateAfterTx(driftClient, userClient);
					}
				}

				return true;
			} catch (err: any) {
				console.error(err);
				TransactionErrorHandler.handleError({
					error: err,
					toastId,
					wallet: wallet as Adapter,
				});
				return false;
			}
		},
		[driftClient, wallet, accountsGet, getTxParams]
	);

	return { addInsuranceFundStake };
};
